import { HideContainer } from '@components/hide-container';
import { IconSend } from '@components/icons/send';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { AttributeFieldType } from 'api/attributes/requests';
import { createTravellerJourneyActionFieldPath } from 'api/documents/requests';
import { useJourneyAttributes } from 'api/journeys/hooks';
import {
  useActivityLogs,
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { JourneyField, TravellerJourneyActionDetailed } from 'api/types';
import { useUsers } from 'api/users/hooks';
import { ACTION_TYPES } from 'ui/actions/action-config';

import { UpdateFieldsFormContent } from './helpers';

import {
  ActionProgressBar,
  RenderAttributeFieldLabelGroup,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TravellerActionHeader,
  isActionError,
  useActionTabs,
  useEditingContext,
} from '../common';

const ACTION_TYPE = ACTION_TYPES.update_fields;

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  const travellerActionQuery = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  const { data: activityLogs } = useActivityLogs(shortId);
  const activityCount = activityLogs?.length ?? 0;
  const action = travellerActionQuery.data;

  const { tabs } = useActionTabs({
    actionId: shortId ?? '',
    activityCount,
    display: action ? <DetailsContent action={action} /> : <></>,
    edit: action ? <FormContent action={action} /> : <></>,
  });

  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !action) {
    return <div>{t('dataNotFound')}</div>;
  }

  return (
    <Sidebar>
      <TravellerActionHeader actionType={ACTION_TYPE} tabs={tabs}>
        <TravellerActionActions
          action={action}
          ratioOverride={action.status === 'completed' ? 1 : 0}
          statusOverride={action.status}
        />
      </TravellerActionHeader>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const status = action.status;
  const isError = isActionError(action);

  const fields = (action.data.fields ?? []) as JourneyField[];
  const updatedFields = (action.data.updatedFields ?? []) as {
    field: JourneyField;
    fieldTitle: string;
    fieldType: AttributeFieldType;
    optionValue: string;
  }[];

  const attributesQuery = useJourneyAttributes(action.journeyId);
  const attributes = attributesQuery.data ?? [];
  const userQuery = useUsers();
  const usersList = userQuery.data;

  if (attributesQuery.isLoading) {
    return <Spinner />;
  }

  return (
    <Stack space={6}>
      <ActionProgressBar action={action} isError={isError} isCard={false} />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <LabelTextGroup label={t('description')} text={action.description} />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('fields')}>
        <Stack space={4}>
          {fields.map((field, idx) => (
            <RenderAttributeFieldLabelGroup
              key={idx}
              attribute={attributes.find((attribute) => attribute.shortId === field.field)}
              field={field}
              usersList={usersList}
              savedFieldName={
                updatedFields.find((ufield) => ufield.field.field === field.field)?.fieldTitle
              }
              savedFieldType={
                updatedFields.find((ufield) => ufield.field.field === field.field)?.fieldType ??
                field.last_saved_type
              }
              savedOptionValue={
                updatedFields.find((ufield) => ufield.field.field === field.field)?.optionValue
              }
              actionStatus={status}
            />
          ))}
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      {status !== 'completed' && (
        <TravellerActionFooter
          action={action}
          startIcon={<IconSend />}
          startText={t('run')}
          retryText={t('retry')}
        />
      )}
    </Stack>
  );
};

const FormContent = ({ action }: ContentProps) => {
  const { stopEditing } = useEditingContext();

  const editMutation = useUpdateJourneyTravellerAction(action, { onSuccess: stopEditing });

  const filePath = createTravellerJourneyActionFieldPath(action);

  return (
    <UpdateFieldsFormContent
      action={action}
      journeyId={action.journeyId}
      filePath={filePath}
      onCancel={stopEditing}
      onSubmit={(data) =>
        editMutation.mutateAsync({
          isAutomatic: true,
          action: 'update_fields',
          values: { fields: data.fields },
          description: data.description,
        })
      }
    />
  );
};

const sidebarLocale = {
  en: {
    actionDetails: 'Action details',
    description: 'Description',
    fields: 'Fields',
    dataNotFound: 'Data not found',
    run: 'Run',
    retry: 'Retry',
  },
};
